import { IOperation, ClientError } from '@chainlit/react-client';
import { ICreateOperationRequest, IUpdateOperationRequest } from '@/types/operations';

// API service for operations using the existing Chainlit API client
// Usage: Pass the apiClient from ChainlitContext (useContext(ChainlitContext))
class OperationsService {
  async getAllOperations(apiClient: any): Promise<IOperation[]> {
    try {
      if (typeof apiClient?.listOperations === 'function') {
        return await apiClient.listOperations();
      }
      const res = await apiClient.get('/project/operations');
      return res.json();
    } catch (error) {
      if (error instanceof ClientError) {
        throw new Error(`Failed to fetch operations: ${error.toString()}`);
      }
      throw error;
    }
  }

  async getOperation(apiClient: any, id: string): Promise<IOperation | null> {
    try {
      if (typeof apiClient?.getOperation === 'function') {
        return await apiClient.getOperation(id);
      }
      const res = await apiClient.get(`/project/operations/${id}`);
      return res.json();
    } catch (error) {
      if (error instanceof ClientError && error.status === 404) {
        return null;
      }
      if (error instanceof ClientError) {
        throw new Error(`Failed to fetch operation: ${error.toString()}`);
      }
      throw error;
    }
  }

  // Add alias for backward compatibility
  async getOperationById(apiClient: any, id: string): Promise<IOperation | null> {
    return this.getOperation(apiClient, id);
  }

  async createOperation(apiClient: any, request: ICreateOperationRequest): Promise<IOperation> {
    try {
      if (typeof apiClient?.createOperation === 'function') {
        return await apiClient.createOperation(request);
      }
      const res = await apiClient.post('/project/operations', request);
      return res.json();
    } catch (error) {
      if (error instanceof ClientError) {
        throw new Error(`Failed to create operation: ${error.toString()}`);
      }
      throw error;
    }
  }

  async updateOperation(apiClient: any, id: string, request: IUpdateOperationRequest): Promise<IOperation> {
    try {
      if (typeof apiClient?.updateOperation === 'function') {
        await apiClient.updateOperation(id, { ...request, id });
      } else {
        const res = await apiClient.put(`/project/operations/${id}`, { ...request, id });
        await res.json();
      }
      // After successful update, fetch the updated operation
      const updatedOperation = await this.getOperation(apiClient, id);
      if (!updatedOperation) {
        throw new Error('Operation not found after update');
      }
      return updatedOperation;
    } catch (error) {
      if (error instanceof ClientError) {
        throw new Error(`Failed to update operation: ${error.toString()}`);
      }
      throw error;
    }
  }

  async deleteOperation(apiClient: any, id: string): Promise<void> {
    try {
      if (typeof apiClient?.deleteOperation === 'function') {
        await apiClient.deleteOperation(id);
      } else {
        const res = await apiClient.delete(`/project/operations/${id}`, {});
        await res.json();
      }
    } catch (error) {
      if (error instanceof ClientError) {
        throw new Error(`Failed to delete operation: ${error.toString()}`);
      }
      throw error;
    }
  }

  async addThreadToOperation(apiClient: any, operationId: string, threadId: string): Promise<void> {
    try {
      if (typeof apiClient?.addThreadToOperation === 'function') {
        await apiClient.addThreadToOperation(operationId, threadId);
      } else {
        const res = await apiClient.post(`/project/operations/${operationId}/threads`, {
          thread_id: threadId,
          operation_id: operationId
        });
        await res.json();
      }
    } catch (error) {
      if (error instanceof ClientError) {
        throw new Error(`Failed to add thread to operation: ${error.toString()}`);
      }
      throw error;
    }
  }

  async removeThreadFromOperation(apiClient: any, operationId: string, threadId: string): Promise<void> {
    try {
      if (typeof apiClient?.removeThreadFromOperation === 'function') {
        await apiClient.removeThreadFromOperation(operationId, threadId);
      } else {
        const res = await apiClient.delete(`/project/operations/${operationId}/threads/${threadId}`, {});
        await res.json();
      }
    } catch (error) {
      if (error instanceof ClientError) {
        throw new Error(`Failed to remove thread from operation: ${error.toString()}`);
      }
      throw error;
    }
  }

  async getOperationsForThread(apiClient: any, threadId: string): Promise<IOperation[]> {
    try {
      if (typeof apiClient?.getOperationsForThread === 'function') {
        return await apiClient.getOperationsForThread(threadId);
      }
      const res = await apiClient.get(`/project/threads/${threadId}/operations`);
      return res.json();
    } catch (error) {
      if (error instanceof ClientError) {
        throw new Error(`Failed to fetch operations for thread: ${error.toString()}`);
      }
      throw error;
    }
  }

  async getThreadsForOperation(apiClient: any, operationId: string): Promise<any[]> {
    try {
      if (typeof apiClient?.getThreadsForOperation === 'function') {
        return await apiClient.getThreadsForOperation(operationId);
      }
      const res = await apiClient.get(`/project/operations/${operationId}/threads`);
      return res.json();
    } catch (error) {
      if (error instanceof ClientError) {
        throw new Error(`Failed to fetch threads for operation: ${error.toString()}`);
      }
      throw error;
    }
  }

  async exportOperationData(apiClient: any, operationId: string): Promise<any> {
    try {
      if (typeof apiClient?.exportOperationData === 'function') {
        return await apiClient.exportOperationData(operationId);
      }
      const res = await apiClient.get(`/project/operations/${operationId}/export`);
      return res.json();
    } catch (error) {
      if (error instanceof ClientError) {
        throw new Error(`Failed to export operation data: ${error.toString()}`);
      }
      throw error;
    }
  }
}

export const operationsService = new OperationsService();